import { css, CSSObject, SerializedStyles } from '@emotion/react';
import {
    createResponsiveStyles,
    DialogSize,
    objectToCssVariables,
    ResponsiveStyleValue,
    useHoneycombTheme,
    PaperCornerStyle,
    DialogFullScreen,
    DialogPosition,
    SpacingValue,
} from '../../theming';

type StyleClasses = 'root' | 'surface' | 'backdrop' | 'bar' | 'header' | 'content';

function getFullScreenResponsiveValue(fullScreen: DialogFullScreen): ResponsiveStyleValue<boolean> {
    if (fullScreen === 'mobile-only') {
        return { xs: true, m: false };
    }
    if (fullScreen === 'always') {
        return true;
    }
    return false;
}

function isDialogWidth(value: string): value is DialogSize {
    return ['xs', 's', 'm', 'l', 'xl'].includes(value);
}

// Static styles
export const DIALOG_STATIC_STYLES: { [key in StyleClasses]: SerializedStyles } = {
    root: css`
        z-index: var(--hc-z-index-modal);

        &[data-position='centered'] {
            display: grid;
            place-items: center;
        }
    `,
    surface: css`
        outline: none;
        position: relative;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        background-color: var(--hc-color-background-content);

        // DRAWER VARIANT
        &[data-position^='drawer-'] {
            position: fixed;
            inset: 0;

            // Slide-in animation
            transition-property: transform;
            transition-timing-function: var(--hc-transition-easing-ease-in-out);

            &[data-position='drawer-left'] {
                inset-inline-end: auto;
            }

            &[data-position='drawer-right'] {
                inset-inline-start: auto;
            }

            &[data-position='drawer-top'] {
                bottom: auto;
            }

            &[data-position='drawer-bottom'] {
                top: auto;
            }
        }

        // CENTERED VARIANT
        &[data-position='centered'] {
            // Fade-in and scale animation
            transition-property: opacity, transform;
            transition-timing-function: var(--hc-transition-easing-ease-in-out);
        }

        // TRANSITION RULES
        &[data-transition-status='open'] {
            transition-duration: var(--dialog-duration-open);

            &[data-position='centered'] {
                opacity: 1;
                transform: scale(1);
            }
            &[data-position^='drawer-'] {
                transform: translate3d(0, 0, 0);
            }
        }

        &[data-transition-status='close'] {
            transition-duration: var(--dialog-duration-close);
        }

        &[data-transition-status='initial'],
        &[data-transition-status='close'],
        &[data-transition-status='unmounted'] {
            &[data-position='centered'] {
                opacity: 0;
                transform: scale(0.8);
            }

            &[data-position^='drawer-'] {
                &[data-position='drawer-left'] {
                    transform: translateX(-100%);
                    &:dir(rtl) {
                        transform: translateX(100%);
                    }
                }
                &[data-position='drawer-right'] {
                    transform: translateX(100%);
                    &:dir(rtl) {
                        transform: translateX(-100%);
                    }
                }
                &[data-position='drawer-top'] {
                    transform: translateY(-100%);
                }
                &[data-position='drawer-bottom'] {
                    transform: translateY(100%);
                }
            }
        }

        &[data-animation-disabled] {
            transition: none !important;
        }
    `,
    backdrop: css`
        position: absolute;
        inset: 0;

        background-color: var(--dialog-backdrop-color);
        backdrop-filter: var(--dialog-backdrop-filter);

        transition-property: opacity;
        transition-timing-function: var(--hc-transition-easing-ease-in-out);

        &[data-transition-status='initial'],
        &[data-transition-status='close'],
        &[data-transition-status='unmounted'] {
            opacity: 0;
        }

        &[data-transition-status='open'],
        &[data-transition-status='loading'] {
            opacity: 1;
            transition-duration: var(--dialog-duration-open);
        }

        &[data-transition-status='close'] {
            transition-duration: var(--dialog-duration-close);
        }

        &[data-animation-disabled] {
            transition: none !important;
        }
    `,
    bar: css`
        flex: 0 0 auto;
        background: var(--hc-color-background-content);
        min-height: var(--dialog-bar-min-height);
        padding-top: var(--dialog-bar-padding-y);
        padding-bottom: var(--dialog-bar-padding-y);
        z-index: 1;

        &[data-dialog-header][data-border] {
            border-bottom: solid 1px var(--hc-color-border);
        }

        &[data-dialog-footer][data-border] {
            border-top: solid 1px var(--hc-color-border);
        }
    `,
    header: css`
        display: flex;
        flex-direction: column;

        [data-wrapper] {
            display: flex;
            flex-grow: 1;
            justify-content: space-between;
            align-items: center;
            gap: calc(var(--hc-spacing-unit) * 5);

            [data-dialog-title] {
                flex-grow: 1;
            }
            [data-close-button] {
                flex-shrink: 0;
            }
        }
    `,
    content: css`
        flex: 1 1 auto;
        overflow-y: auto;
    `,
};

type SurfaceVariantStyles = {
    normal: CSSObject;
    fullscreen: CSSObject;
};

type DynamicSurfaceStyles = {
    centered: SurfaceVariantStyles;
    drawer: SurfaceVariantStyles;
};

const DYNAMIC_SURFACE_STYLES: DynamicSurfaceStyles = {
    centered: {
        normal: {
            width: 'var(--dialog-size)',
            height: 'auto',
            maxWidth: 'calc(100dvw - var(--dialog-margin) * 2)',
            maxHeight: 'calc(100dvh - var(--dialog-margin) * 2)',
            borderRadius: 'var(--dialog-border-radius)',
        },
        fullscreen: {
            width: '100vw',
            height: '100dvh',
            maxWidth: 'none',
            maxHeight: 'none',
            borderRadius: 0,
        },
    },
    drawer: {
        normal: {
            "&[data-position='drawer-left'], &[data-position='drawer-right']": {
                width: 'var(--dialog-size)',
                maxWidth: 'calc(100% - var(--dialog-margin))',
            },
            "&[data-position='drawer-top'], &[data-position='drawer-bottom']": {
                height: 'var(--dialog-size)',
                maxHeight: 'calc(100% - var(--dialog-margin))',
            },
            "&[data-position='drawer-top']": {
                borderEndStartRadius: 'var(--dialog-border-radius)',
                borderEndEndRadius: 'var(--dialog-border-radius)',
            },
            "&[data-position='drawer-bottom']": {
                borderStartStartRadius: 'var(--dialog-border-radius)',
                borderStartEndRadius: 'var(--dialog-border-radius)',
            },
        },
        fullscreen: {
            "&[data-position='drawer-left'], &[data-position='drawer-right']": {
                width: '100%',
            },
            "&[data-position='drawer-top'], &[data-position='drawer-bottom']": {
                height: '100%',
            },
            "&[data-position='drawer-top']": {
                borderEndStartRadius: 0,
                borderEndEndRadius: 0,
            },
            "&[data-position='drawer-bottom']": {
                borderStartStartRadius: 0,
                borderStartEndRadius: 0,
            },
        },
    },
};

export function useDialogDynamicSurfaceStyles(
    fullScreen: DialogFullScreen,
    position: DialogPosition
): CSSObject | null {
    const { theme } = useHoneycombTheme();

    const isCentered = position === 'centered';

    if (isCentered) {
        // Dymnamic styles pertaining to centered dialogs.
        return createResponsiveStyles(
            [
                {
                    value: getFullScreenResponsiveValue(fullScreen),
                    getStyles: (value: boolean): CSSObject => {
                        return value
                            ? DYNAMIC_SURFACE_STYLES.centered.fullscreen
                            : DYNAMIC_SURFACE_STYLES.centered.normal;
                    },
                },
            ],
            theme
        );
    }

    // Dymnamic styles pertaining to drawer dialogs.
    return createResponsiveStyles(
        [
            {
                value: getFullScreenResponsiveValue(fullScreen),
                getStyles: (value: boolean): CSSObject => {
                    return value ? DYNAMIC_SURFACE_STYLES.drawer.fullscreen : DYNAMIC_SURFACE_STYLES.drawer.normal;
                },
            },
        ],
        theme
    );
}

export function useDialogRootVariables(
    size: DialogSize | string,
    cornerStyle: PaperCornerStyle,
    margin: SpacingValue
): CSSObject {
    const { theme } = useHoneycombTheme();
    const dialogTheme = theme.components.themes.Dialog;
    const { animationDuration } = dialogTheme;

    const sizeValue = isDialogWidth(size?.toString() || '') ? `var(--dialog-size-${size})` : size;
    const marginValue = theme.fn.getSpacingValue(margin);

    return {
        '--dialog-duration-open': `${animationDuration.open}ms`,
        '--dialog-duration-close': `${animationDuration.close}ms`,
        '--dialog-border-radius': cornerStyle === 'rounded' ? dialogTheme.radius : '0',
        '--dialog-bar-min-height': `${dialogTheme.bar.minHeight}px`,
        '--dialog-bar-padding-y': `${dialogTheme.bar.paddingY}px`,
        ...objectToCssVariables(dialogTheme.size, 'dialog-size-'),
        '--dialog-size': sizeValue,
        '--dialog-margin': marginValue,
    };
}
